<template>
  <div v-if="errorCaused"></div>
  <div v-else-if="overviewData">
    <div
      v-if="overviewData.content.image"
      class="max-h-[300px] overflow-hidden"
    >
      <CmsImage :blok="overviewData.content.image[0]" />
    </div>
    <div class="relative">
      <SectionBackground class="hidden bg-white md:block" />
      <div class="flex justify-center gap-4 py-7 text-lg">
        <TagList
          :tags="tags"
          :selected-tag-uuids="tagIds"
          show-deselect-icon
          placement="header"
        />
      </div>
      <div
        class="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-5 md:grid-cols-2 md:pb-8"
      >
        <template v-if="!fetching">
          <BlogTeaser
            v-for="article in articles"
            :key="article.id"
            :blok="article"
            :selected-tag-uuids="tagIds"
            class="border-b border-primary md:border-b-0"
          />
        </template>
        <BlogTeaserSkeleton v-else :count="MAGAZINE_ITEMS_PER_PAGE" />
      </div>
    </div>
    <AgsLazy placeholder-ratio="10/1" ssr>
      <template #lazy="{ visible }">
        <Pagination
          v-if="visible && lastPage > 1"
          :current-page="currentPage"
          :first-page="MAGAZINE_FIRST_PAGE"
          :last-page="lastPage"
          class="mb-12 mt-8"
        />
      </template>
    </AgsLazy>
    <div class="mx-auto max-w-6xl px-5">
      <NuxtLazyHydrate when-visible>
        <MoreArticles
          :blog-overview="overviewData"
          carousel-container-class="-mx-5 md:mx-0"
        />
      </NuxtLazyHydrate>
      <NuxtLazyHydrate v-if="tags" when-visible>
        <SectionHeadline>
          {{ $t('blog.more_topics') }}
        </SectionHeadline>
        <MoreTopics :topics="tags" />
      </NuxtLazyHydrate>
    </div>
  </div>
</template>

<script setup lang="ts">
import { watch, computed, ref, defineOptions } from 'vue'
import { definePageMeta } from '#imports'
import { createError } from '#app/composables/error'
import { fetchOnNavigation } from '#layers/base/utils/loading'
import useStoryMetaData from '~/composables/useStoryMetaData'
import { useRoute } from '#app/composables/router'
import useBlogArticles from '#layers/base/composables/useBlogArticles'
import {
  MAGAZINE_FIRST_PAGE,
  MAGAZINE_ITEMS_PER_PAGE,
} from '#layers/base/constants/pagination'
import type {
  SbBlogArticleV2,
  SbBlogOverviewV2,
  Sbtag,
  StoryblokStory,
} from '~/cms/storyblok/types/storyblok.d.ts'

useRouteCache((helper) => helper.setMaxAge(3600).setCacheable())

const { data, total, fetchMany, fetching } = useBlogArticles<SbBlogArticleV2[]>(
  'blog-articles-listing-posts',
  true,
)

const { data: overviewData, fetchBlogOverview } =
  useBlogArticles<SbBlogOverviewV2>('blog-articles-listing-rest')

const route = useRoute()
const errorCaused = ref(false)
const tagIds = computed(() => {
  const tag = route?.query.tag
  if (!tag || !tag.length) {
    return
  }
  return Array.isArray(tag) ? tag : [tag]
})

const currentPage = computed(
  () => Number(route.query.page) || MAGAZINE_FIRST_PAGE,
)
const lastPage = computed(() =>
  Math.ceil(total.value / MAGAZINE_ITEMS_PER_PAGE),
)

const articles = computed(() =>
  Array.isArray(data.value)
    ? (data.value as StoryblokStory<SbBlogArticleV2>[])
    : [],
)

const metaDataOverrides = ref<Partial<Record<'robots' | 'canonical', string>>>({
  robots: 'index,follow,max-image-preview:large',
  ...(route.query.tag && { canonical: 'skip' }),
})
useStoryMetaData({
  data: overviewData,
  overrides: metaDataOverrides,
  fetching,
})

const fetchData = async () => {
  await Promise.all([
    fetchBlogOverview(),
    fetchMany({
      page: currentPage.value,
      perPage: MAGAZINE_ITEMS_PER_PAGE,
      tagIds: tagIds.value,
      resolveTags: true,
    }),
  ])
}

await fetchOnNavigation(async () => {
  await fetchData()

  if (!overviewData.value?.content) {
    errorCaused.value = true
    throw createError({
      statusCode: 404,
      message: 'Not found',
    })
  }
})

// A single watch owns the refetch: a ?page or ?tag change triggers exactly
// one fetchMany (avoids a tag/page double-fetch race). Tag links drop the
// page param, so selecting a tag naturally resets to page 1.
watch(
  () => [currentPage.value, tagIds.value],
  async () => {
    window.scrollTo({ top: 0, behavior: 'instant' })
    metaDataOverrides.value.canonical = tagIds.value ? 'skip' : undefined
    await fetchMany({
      page: currentPage.value,
      perPage: MAGAZINE_ITEMS_PER_PAGE,
      tagIds: tagIds.value,
      resolveTags: true,
    })
  },
)

const tags = computed(
  () =>
    (overviewData.value?.content?.tags?.filter(
      (tag) => typeof tag !== 'string',
    ) as StoryblokStory<Sbtag>[]) ?? [],
)

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    de: '/magazin',
    ch: '/magazin',
    at: '/magazin',
  },
})

defineOptions({ name: 'MagazinePages' })
definePageMeta({ pageType: 'static' })
</script>
